import { Box, Text, useColorMode } from '@chakra-ui/react';
import { Point2 } from '../../../../domain/geometry/geometric-types';
import { kilerMap } from '../../ProductChoices';
import { getPolylineMidpoint } from '../../../../domain/geometry/algorithms/util/line-segment';
import { toPoints2 } from '../../../../domain/geometry/algorithms/util/type-mapping';
import {
  GetSparkelPropertiesForProjectQuery,
  SheetScaleType,
  SheetShapeDeepFragment,
} from '../../../../gql/graphql';
import { getDiamond, getWedgeFromLineShape } from './DiamondLabel';
import { onlyOneSide } from './kile-selector';

type WedgeLabelProps = {
  shape: SheetShapeDeepFragment;
  getPointInViewerCoordinateSystem: (pointInPdf: Point2) => Point2;
  propertyData: GetSparkelPropertiesForProjectQuery | undefined;
  calibration: SheetScaleType;
};

// Helper to create a wedge label based on product length
const createWedgeLabel = (productLength: number): string => {
  // Round to 1 decimal place for consistent display
  return `${productLength.toFixed(1)}m wedge`;
};

// Component to render a single wedge label
const WedgeLabelText = ({
  position,
  label,
  colorMode,
}: {
  position: Point2;
  label: string;
  colorMode: string;
}) => {
  if (!label) return null;

  return (
    <Box
      position={'absolute'}
      top={position[1]}
      left={position[0]}
      transform="translate(-50%, -50%)"
      pointerEvents={'none'}
    >
      <Text
        color={'blue.500'}
        filter={`brightness(${colorMode === 'light' ? 40 : 160}%)`}
        fontWeight={'semibold'}
        fontSize={'2xs'}
      >
        {label}
      </Text>
    </Box>
  );
};

// Helper to create side-specific labels
const createSideLabels = (
  diamond: {
    triangle1: boolean;
    triangle2: boolean;
    triangle3: boolean;
    triangle4: boolean;
  },
  productLength: number
) => {
  const leftActive = [diamond.triangle1, diamond.triangle2].filter(
    Boolean
  ).length;
  const rightActive = [diamond.triangle3, diamond.triangle4].filter(
    Boolean
  ).length;

  // Always use the same product length for all labels, rounded to 1 decimal place
  const roundedLength = productLength.toFixed(1);

  // Simply show the product length if the side is active, or empty string if not
  const leftSideLabel = leftActive > 0 ? `${roundedLength}m wedge` : '';
  const rightSideLabel = rightActive > 0 ? `${roundedLength}m wedge` : '';

  return { leftSideLabel, rightSideLabel, leftActive, rightActive };
};

// Helper to calculate label positions for two-sided wedges
const calculateLabelPositions = (
  wedgePoints: Point2[],
  getPointInViewerCoordinateSystem: (point: Point2) => Point2
) => {
  const wedgeStart = wedgePoints[0];
  const wedgeEnd = wedgePoints[wedgePoints.length - 1];

  // Calculate positions at 1/3 and 2/3 along the segment
  const wedgeStartMidpoint = [
    0.66 * wedgeStart[0] + 0.34 * wedgeEnd[0],
    0.66 * wedgeStart[1] + 0.34 * wedgeEnd[1],
  ] as Point2;

  const wedgeEndMidpoint = [
    0.34 * wedgeStart[0] + 0.66 * wedgeEnd[0],
    0.34 * wedgeStart[1] + 0.66 * wedgeEnd[1],
  ] as Point2;

  const leftPosition = getPointInViewerCoordinateSystem(wedgeStartMidpoint);
  const rightPosition = getPointInViewerCoordinateSystem(wedgeEndMidpoint);

  return { leftPosition, rightPosition };
};

// Component for one-sided wedge labels
const OneSidedWedgeLabel = ({
  wedgePoints,
  wedgeLabel,
  getPointInViewerCoordinateSystem,
  colorMode,
}: {
  wedgePoints: Point2[];
  wedgeLabel: string;
  getPointInViewerCoordinateSystem: (point: Point2) => Point2;
  colorMode: string;
}) => {
  const position = getPointInViewerCoordinateSystem(
    getPolylineMidpoint(wedgePoints)
  );
  return (
    <WedgeLabelText
      position={position}
      label={wedgeLabel}
      colorMode={colorMode}
    />
  );
};

// Component for two-sided wedge labels
const TwoSidedWedgeLabel = ({
  wedgePoints,
  diamond,
  productLength,
  getPointInViewerCoordinateSystem,
  colorMode,
}: {
  wedgePoints: Point2[];
  diamond: {
    triangle1: boolean;
    triangle2: boolean;
    triangle3: boolean;
    triangle4: boolean;
  };
  productLength: number;
  getPointInViewerCoordinateSystem: (point: Point2) => Point2;
  colorMode: string;
}) => {
  const { leftPosition, rightPosition } = calculateLabelPositions(
    wedgePoints,
    getPointInViewerCoordinateSystem
  );

  const { leftSideLabel, rightSideLabel, leftActive, rightActive } =
    createSideLabels(diamond, productLength);

  return (
    <>
      {leftActive > 0 && (
        <WedgeLabelText
          position={leftPosition}
          label={leftSideLabel}
          colorMode={colorMode}
        />
      )}
      {rightActive > 0 && (
        <WedgeLabelText
          position={rightPosition}
          label={rightSideLabel}
          colorMode={colorMode}
        />
      )}
    </>
  );
};

const WedgeLabel = ({
  shape,
  getPointInViewerCoordinateSystem,
  propertyData,
  calibration,
}: WedgeLabelProps) => {
  const { colorMode } = useColorMode();

  if (!shape.sheetShapeLine || !propertyData) {
    return null;
  }

  const diamond = getDiamond(propertyData, shape.dbId, shape.urn);
  const isOneSided = onlyOneSide(diamond);
  const wedgePoints = toPoints2(shape.sheetShapeLine.points);

  // Get the wedge type from the line shape
  const wedgeType = getWedgeFromLineShape(
    shape,
    wedgePoints,
    propertyData,
    calibration
  );

  // Get the product length from the catalog
  const productLength = wedgeType ? kilerMap[wedgeType][0]?.length : 0;

  if (!productLength || Object.values(diamond).every((triangle) => !triangle)) {
    return null;
  }

  // Count active triangles and create the main label
  const activeTriangles = Object.values(diamond).filter(Boolean).length;
  const wedgeLabel = createWedgeLabel(productLength);

  // Render different label components based on whether it's one-sided or two-sided
  if (isOneSided) {
    return (
      <OneSidedWedgeLabel
        wedgePoints={wedgePoints}
        wedgeLabel={wedgeLabel}
        getPointInViewerCoordinateSystem={getPointInViewerCoordinateSystem}
        colorMode={colorMode}
      />
    );
  }

  return (
    <TwoSidedWedgeLabel
      wedgePoints={wedgePoints}
      diamond={diamond}
      productLength={productLength}
      getPointInViewerCoordinateSystem={getPointInViewerCoordinateSystem}
      colorMode={colorMode}
    />
  );
};

export default WedgeLabel;
